/**
 * ⑨評価結果の表示に使う整形(#75)。
 *
 * 元データは worker が GCS へ置く evaluation_result.json(CompareResult 相当)。
 * Markdown レポートを流し込むのをやめ、構造化された結果から画面を組む。
 * **集計はここで数え直さない。** score と coverage は CLI 側がすでに算出しており、
 * 画面で計算し直すと同じ値の出どころが二つになってズレる(#21 #40 と同じ理由)。
 */

export type Verdict = "covered" | "differs" | "missing";
export type Category = "critical_point" | "ingredient" | "heat_level" | "plating";

export interface EvaluationItem {
  category: Category;
  item: string;
  verdict: Verdict;
  evidence: string | null;
  feedback: string;
  /** 由来する工程(#122)。古い結果には無い */
  step_no?: number | null;
  /** 根拠が見える時刻(作業動画の先頭からの秒。#126) */
  at_sec?: number | null;
  /** 作業動画から切り出したコマ・クリップ。評価成果物からの相対パス(#126) */
  frame?: string | null;
  clip?: string | null;
}

/** 秒を mm:ss に。作業者が動画のどこかを一目で分かるようにする(#126)。 */
export function mmss(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

export interface EvaluationUnknown {
  category: Category;
  text: string;
  /** 由来する工程(#122)。古い結果には無い */
  step_no?: number | null;
  /** 保留の理由(#128)。古い結果には無い */
  reason?: "not_observed" | "no_reason_given";
}

export interface EvaluationResult {
  golden_dish: string;
  trainee_dish: string;
  items: EvaluationItem[];
  extra_items: { category: Category; item: string; feedback: string }[];
  overall_feedback: string;
  score: number;
  coverage?: Record<string, number>;
  unknowns?: EvaluationUnknown[];
  /** マニュアルの工程の並びと名前(#122)。古い結果には無い */
  steps?: { step_no: number; name: string }[];
}

export const CATEGORY_LABEL: Record<Category, string> = {
  critical_point: "重要ポイント",
  ingredient: "食材",
  heat_level: "火加減",
  plating: "盛り付け",
};

/** デモ画面(public/demo)の並び順に合わせる */
export const CATEGORY_ORDER: Category[] = ["critical_point", "ingredient", "heat_level", "plating"];

export const VERDICT_LABEL: Record<Verdict, string> = {
  covered: "できている",
  differs: "基準と違う",
  missing: "見当たらない",
};

export type Band = "good" | "mid" | "low";

/** デモの凡例と同じ区切り(80以上=良好 / 60-79=継続指導 / 60未満=要重点指導) */
export const BAND_LABEL: Record<Band, string> = {
  good: "良好",
  mid: "継続指導",
  low: "要重点指導",
};

export function scoreBand(score: number): Band {
  if (score >= 80) return "good";
  if (score >= 60) return "mid";
  return "low";
}

export interface CategoryCoverage {
  category: Category;
  label: string;
  /** 0-1。CLI が算出した coverage をそのまま使う */
  ratio: number;
  ok: number;
  total: number;
}

export interface EvaluationView {
  dishName: string;
  score: number;
  band: Band;
  /** 直したいところ。基準と違う → 見当たらない の順に出す(直す優先度が高い方から) */
  problems: EvaluationItem[];
  covered: EvaluationItem[];
  unknowns: EvaluationUnknown[];
  extras: { category: Category; item: string; feedback: string }[];
  overallFeedback: string;
  coverage: CategoryCoverage[];
  /**
   * マニュアルと同じ工程の並びにまとめた結果(#122)。項目が工程番号を持たない古い結果では空で、
   * 画面は従来どおり判定別の並びだけを出す
   */
  bySteps: StepGroup[];
  /** 工程番号 → 工程名。名前の無い古い結果では空 */
  stepNames: Record<number, string>;
}

/** 工程ごとの結果の1項目。unknown は確認できなかった項目 */
export interface StepEntry {
  category: Category;
  text: string;
  verdict: Verdict | "unknown";
}

export interface StepGroup {
  /** null は工程に紐づかない項目(盛り付けなど) */
  stepNo: number | null;
  /** 工程名。古い結果では null(画面は「工程N」とだけ出す) */
  name: string | null;
  entries: StepEntry[];
}

const VERDICT_RANK: Record<Verdict, number> = { differs: 0, missing: 1, covered: 2 };

export function toView(r: EvaluationResult): EvaluationView {
  const items = r.items ?? [];
  const problems = items
    .filter((i) => i.verdict !== "covered")
    .sort(
      (a, b) =>
        VERDICT_RANK[a.verdict] - VERDICT_RANK[b.verdict] ||
        CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category),
    );
  const covered = items.filter((i) => i.verdict === "covered");

  const coverage: CategoryCoverage[] = [];
  for (const c of CATEGORY_ORDER) {
    const inCat = items.filter((i) => i.category === c);
    if (inCat.length === 0) continue;
    const ok = inCat.filter((i) => i.verdict === "covered").length;
    coverage.push({
      category: c,
      label: CATEGORY_LABEL[c],
      // coverage が来ていれば正。無い場合(古い結果)だけ項目から補う
      ratio: r.coverage?.[c] ?? (inCat.length ? ok / inCat.length : 0),
      ok,
      total: inCat.length,
    });
  }

  const stepNames: Record<number, string> = {};
  for (const st of r.steps ?? []) stepNames[st.step_no] = st.name;

  return {
    dishName: r.golden_dish || r.trainee_dish || "",
    score: r.score ?? 0,
    band: scoreBand(r.score ?? 0),
    problems,
    covered,
    unknowns: r.unknowns ?? [],
    extras: r.extra_items ?? [],
    overallFeedback: (r.overall_feedback ?? "").trim(),
    coverage,
    bySteps: groupBySteps(r),
    stepNames,
  };
}

const ENTRY_RANK: Record<StepEntry["verdict"], number> = { differs: 0, missing: 1, unknown: 2, covered: 3 };

/**
 * 評価の項目をマニュアルの工程の並びにまとめる(#122)。
 *
 * 判定別(直したいところ/できていたところ)に全工程を混ぜて並べると、指摘を読んでも
 * マニュアルのどこを見直せばよいか分からない。工程ごとに、その工程の項目を
 * 直したい順(基準と違う → 見当たらない → 確認できず → できている)で並べる。
 * 工程に紐づかない項目(盛り付けなど)は最後にまとめる。
 */
export function groupBySteps(r: EvaluationResult): StepGroup[] {
  const items = r.items ?? [];
  const unknowns = r.unknowns ?? [];
  const hasStep = [...items, ...unknowns].some((i) => typeof i.step_no === "number");
  if (!hasStep) return [];

  const entries = new Map<number | null, StepEntry[]>();
  const push = (stepNo: number | null | undefined, e: StepEntry) => {
    const k = typeof stepNo === "number" ? stepNo : null;
    entries.set(k, [...(entries.get(k) ?? []), e]);
  };
  for (const i of items) push(i.step_no, { category: i.category, text: i.item, verdict: i.verdict });
  for (const u of unknowns) push(u.step_no, { category: u.category, text: u.text, verdict: "unknown" });

  // 並びはマニュアルの工程順。名前の無い古い結果は工程番号順
  const names = new Map((r.steps ?? []).map((s) => [s.step_no, s.name] as const));
  const order: number[] = [...names.keys()];
  for (const k of [...entries.keys()].sort((a, b) => (a ?? 0) - (b ?? 0))) {
    if (k != null && !order.includes(k)) order.push(k);
  }
  const groups: StepGroup[] = [];
  for (const k of [...order, null]) {
    const es = entries.get(k);
    if (!es?.length) continue;
    groups.push({
      stepNo: k,
      name: k == null ? null : names.get(k) ?? null,
      entries: [...es].sort((a, b) => ENTRY_RANK[a.verdict] - ENTRY_RANK[b.verdict]),
    });
  }
  return groups;
}

/** 「できていたところ」はカテゴリごとにまとめて出す(項目数が多く、並べただけでは読めない) */
export function groupByCategory(items: EvaluationItem[]): { category: Category; label: string; items: EvaluationItem[] }[] {
  return CATEGORY_ORDER.map((c) => ({
    category: c,
    label: CATEGORY_LABEL[c],
    items: items.filter((i) => i.category === c),
  })).filter((g) => g.items.length > 0);
}
